/**
 * previousRecapsModel: pure helpers for the "Previous recaps" panel inside the
 * one page-owned RecapDrawer (2026-09-21).
 *
 * A Lesson Recap is per (lesson x visibility tier), one `documents` doc each,
 * so "the last four lessons" is a grouping problem: pull this student's recap
 * docs newest first, drop the lesson being taught now (its tiers are the live
 * editors above the panel), drop blank tiers (all three are created when a
 * teacher opens the editor, so an untouched tier exists as an empty doc), then
 * take the first four lessons that still hold anything.
 *
 * Teacher names come from src/canon/people.json, keyed by `createdBy`. users/
 * {uid} is readable only by self or admin, so an employee teacher could not
 * resolve a colleague's uid through Firestore; the canon roster is already in
 * the bundle (Avatar, taskModel) and holds nothing that is not public inside
 * the studio.
 */
import people from '../../../../canon/people.json';
import { hasContent } from '../../../../services/lessonRecapModel';
import { STAFF_TIER_ORDER, TEACHER_TIER, tierByVisibility } from './recapTiers';

const NAME_BY_UID = {};
for (const p of people.people || []) {
	if (p.authUid) NAME_BY_UID[p.authUid] = p.displayName;
	if (p.firestoreUid) NAME_BY_UID[p.firestoreUid] = p.displayName;
}

/** First name of a staff uid, or 'Teacher' when the roster does not know it. */
export const teacherFirstName = (uid) => {
	const full = uid ? NAME_BY_UID[uid] : null;
	if (!full) return 'Teacher';
	return String(full).trim().split(/\s+/)[0] || 'Teacher';
};

const toMillis = (value) => {
	if (!value) return 0;
	if (typeof value.toMillis === 'function') return value.toMillis();
	if (typeof value.seconds === 'number') return value.seconds * 1000;
	const parsed = new Date(value).getTime();
	return Number.isNaN(parsed) ? 0 : parsed;
};

const walkText = (node) => {
	if (!node || typeof node !== 'object') return '';
	if (typeof node.text === 'string') return node.text;
	return (node.content || []).map(walkText).join(node.type === 'paragraph' ? '' : '');
};

/** Plain text of a recap doc; falls back to walking the TipTap JSON. */
export const recapPlainText = (recap) => {
	if (typeof recap?.plainText === 'string' && recap.plainText.trim()) return recap.plainText.trim();
	const paragraphs = (recap?.content?.content || []).map(walkText).filter((s) => s.trim());
	return paragraphs.join('\n').trim();
};

// Staff order (Anthony, 2026-09-25). Since 2026-10-05 only the family tiers
// reach the history, so in practice this orders Everyone before Parents.
const TIER_ORDER = STAFF_TIER_ORDER;

/**
 * Group recap docs into one entry per prior lesson, newest first, capped.
 *
 * @param {Array} docs         recap docs (id + data), any order
 * @param {object} opts
 * @param {string} opts.excludeLessonId  the lesson being taught now
 * @param {number} opts.max               lessons to keep (4 by decision)
 */
export const groupPreviousRecaps = (docs = [], { excludeLessonId = null, max = 4 } = {}) => {
	const groups = new Map();
	for (const recap of docs) {
		if (!recap || !hasContent(recap)) continue;
		if (excludeLessonId && recap.lessonId === excludeLessonId) continue;
		// A Teachers only recap is not a recap any more (pass 4 P5.1, 2026-10-05):
		// its words live in the student's Private file, where the migration put
		// them. Dropped here so Previous recaps and the Last lesson row, which
		// both read this, show only what went home.
		if (recap.visibility === TEACHER_TIER.visibility) continue;
		const key = recap.lessonId || `doc:${recap.id}`;
		if (!groups.has(key)) {
			groups.set(key, { key, lessonId: recap.lessonId || null, sort: 0, teacherUids: [], tiers: [] });
		}
		const group = groups.get(key);
		const ms = toMillis(recap.createdAt) || toMillis(recap.updatedAt);
		group.sort = Math.max(group.sort, ms);
		const uid = recap.createdBy || recap.updatedBy || null;
		if (uid && !group.teacherUids.includes(uid)) group.teacherUids.push(uid);
		const tier = tierByVisibility(recap.visibility);
		group.tiers.push({
			id: recap.id,
			visibility: tier.visibility,
			title: tier.title,
			icon: tier.icon,
			privateTier: tier.privateTier,
			text: recapPlainText(recap),
		});
	}
	return [...groups.values()]
		.sort((a, b) => b.sort - a.sort)
		.slice(0, max)
		.map((group) => ({
			key: group.key,
			lessonId: group.lessonId,
			date: group.sort ? new Date(group.sort) : null,
			teacherName: group.teacherUids.map(teacherFirstName).join(' + ') || 'Teacher',
			tiers: group.tiers.sort(
				(a, b) => TIER_ORDER.indexOf(a.visibility) - TIER_ORDER.indexOf(b.visibility)
			),
		}));
};

export const previousRecapDateLabel = (date) =>
	date
		? date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
		: 'Undated';

const previousRecapsModel = { groupPreviousRecaps, teacherFirstName, recapPlainText, previousRecapDateLabel };
export default previousRecapsModel;
